import i18next from 'i18next';
import { RenderedToast } from '@/components/toast';

import {
  ACTION_ERROR_DURATION,
  ACTION_DONE_DURATION,
  ACTION_INFO_DURATION,
  ACTION_WARNING_DURATION,
} from '@Constants/durationConstants';
import { ApiMessagesConstants } from '@Constants/apiMessagesConstants';

import { getMillisecond } from '@Utils/getMillisecond';

const setToastConfig = ({
  toast,
  id,
  position,
  duration,
  onCloseComplete,
  noDuplicate,
  isReplaced,
  toastRef,
  ...rest
}) => {
  const ref = toastRef;
  const config = {
    position,
    duration,
    onCloseComplete,
    render: ({ id, onClose }) => <RenderedToast id={id} onClose={onClose} {...rest} />,
  };

  // 相同 ref 的 toast 同時間只會出現一個，正在顯示中 toast 的會先被 close，才顯示新的 toast
  if (isReplaced) {
    if (ref.current) toast.close(ref.current);

    ref.current = toast({ ...config });
    return;
  }

  // 相同 id 的 toast 同時間只會出現一個
  // 若新的 toast 的 duration 和原先 toast 相同，則 duration 不會被刷新，
  // 如: 原先 duration: 5000ms，在經過 3000ms 後，觸發新的 toast(相同 id，duration 也是 5000ms)，則 2000ms 過後 toast 就會被關閉
  // 和上面指定 ref 的情況不同的是: 新的 toast 只會更新原先 toast 的內容，不會增加新的 toast DOM 節點
  if (noDuplicate && toast.isActive(id)) {
    return toast.update(id, { ...config });
  }

  return toast({ id, ...config });
};

export function popupToast({
  toast,
  id,
  position = 'top',
  title = 'info',
  description = '',
  status = 'info',
  duration,
  isClosable = true,
  onCloseComplete = () => {},
  noDuplicate = true,
  isReplaced,
  toastRef,
}) {
  if (!toast) return;

  let config = {
    title: 'info',
    status: 'info',
    duration: 3000,
  };

  const lowerStatus = status.toLowerCase();

  if (lowerStatus === 'info') {
    config = {
      title: 'info',
      status: 'info',
      duration: ACTION_INFO_DURATION,
    };
  }
  if (lowerStatus === 'success') {
    config = {
      title: 'success',
      status: 'success',
      duration: ACTION_DONE_DURATION,
    };
  }
  if (lowerStatus === 'warning') {
    config = {
      title: 'warning',
      status: 'warning',
      duration: ACTION_WARNING_DURATION,
    };
  }
  if (lowerStatus === 'error') {
    config = {
      title: 'error',
      status: 'error',
      duration: ACTION_ERROR_DURATION,
    };
  }

  return setToastConfig({
    toast,
    toastRef,
    id,
    position,
    title: title || config.title,
    description,
    status: config.status,
    duration: duration || config.duration,
    isClosable,
    onCloseComplete,
    noDuplicate,
    isReplaced,
  });
}

// 403 unauthorized
export function popup403UnauthorizationToast({
  toast,
  id = ApiMessagesConstants.UNAUTHORIZED,
  position = 'top',
  title = i18next.t('common.unauthorizedFeatureMessage'),
  description = '',
  status = 'error',
  duration = getMillisecond(10),
  isClosable = true,
  onCloseComplete = () => {},
  noDuplicate = true,
  isReplaced,
  toastRef,
}) {
  if (!toast) return;

  return setToastConfig({
    toast,
    toastRef,
    id,
    position,
    title,
    description,
    status,
    duration,
    isClosable,
    onCloseComplete,
    noDuplicate,
    isReplaced,
  });
}
